import { useParams } from "react-router"
import { useMemo } from "react"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { missionLogsQuery } from "../MissionPageQueries"
import { DotGraph, DOT_GRAPH_COLS, EmptyDotGraph } from "@nova/component-charts"
import { CardCollection } from "features/pilot-tracking/CardCollection"
import { DistanceUnit, getSpeedDisplay, metersToFeet } from "@nova/format"
import { useAppSelector } from "integrations/redux/hooks"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"

function bucketTimeSeries(values: number[], cols: number): number[] {
    if (values.length === 0) return new Array(cols).fill(0)
    const size = values.length / cols
    return Array.from({ length: cols }, (_, i) => {
        const start = Math.floor(i * size)
        const end = Math.floor((i + 1) * size)
        const slice = values.slice(start, end)
        return slice.length ? slice.reduce((s, v) => s + v, 0) / slice.length : 0
    })
}

function computeMetric(values: number[]) {
    if (values.length === 0) return null
    const avg = values.reduce((s, v) => s + v, 0) / values.length
    const max = Math.max(...values)
    return { avg, max, buckets: bucketTimeSeries(values, DOT_GRAPH_COLS) }
}

export const DotGraphTelemetryCard = () => {
    const { missionId = "" } = useParams()
    const { data: logs } = usePilotTrackingQuery(missionLogsQuery(missionId))
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const altitudeIsMetric =
        distanceUnit === DistanceUnit.Meters || distanceUnit === DistanceUnit.Kilometers
    const altitudeUnit = altitudeIsMetric ? "m" : "ft"
    const { label: speedUnit, convert: toSpeed } = getSpeedDisplay(distanceUnit)

    const metrics = useMemo(() => {
        if (!logs?.length) return null

        const altValues = logs
            .filter((l) => l.altitude !== null)
            .map((l) =>
                altitudeIsMetric ? (l.altitude as number) : metersToFeet(l.altitude as number)
            )
        const speedValues = logs
            .filter((l) => l.speed_horizontal !== null)
            .map((l) => toSpeed(l.speed_horizontal as number))
        const tempValues = logs
            .filter((l) => l.battery_temp !== null)
            .map((l) => l.battery_temp as number)

        return {
            altitude: computeMetric(altValues),
            speed: computeMetric(speedValues),
            temp: computeMetric(tempValues),
        }
    }, [logs, altitudeIsMetric, toSpeed])

    return (
        <CardCollection
            cards={[
                <DotGraphMetric
                    key="alt"
                    label="Altitude"
                    unit={altitudeUnit}
                    avg={metrics?.altitude?.avg}
                    max={metrics?.altitude?.max}
                    buckets={metrics?.altitude?.buckets}
                />,
                <DotGraphMetric
                    key="speed"
                    label="Speed"
                    unit={speedUnit}
                    avg={metrics?.speed?.avg}
                    max={metrics?.speed?.max}
                    buckets={metrics?.speed?.buckets}
                />,
                <DotGraphMetric
                    key="battery"
                    label="Battery Temp"
                    unit="°C"
                    avg={metrics?.temp?.avg}
                    max={metrics?.temp?.max}
                    buckets={metrics?.temp?.buckets}
                />,
            ]}
        />
    )
}

const DotGraphMetric = ({
    label,
    unit,
    avg,
    max,
    buckets,
}: {
    label: string
    unit: string
    avg?: number | null
    max?: number | null
    buckets?: number[] | null
}) => {
    const hasData = !!avg && !!max && !!buckets
    return (
        <div className="flex flex-col gap-3 p-6">
            <div className="flex flex-col gap-1.5">
                <div className="font-mono text-gray-400">Avg. {label}</div>
                <div className="telegraf text-white text-[24px]">
                    {hasData && `${avg.toFixed(0)} ${unit}`}
                    {!hasData && "--"}
                </div>
            </div>
            {hasData ? <DotGraph data={buckets} maxValue={max} /> : <EmptyDotGraph />}
            <small className="font-mono flex items-center gap-2">
                <span className="text-gray-400">Max</span>
                <span className="text-white">
                    {hasData && `${max.toFixed(0)} ${unit}`}
                    {!hasData && "--"}
                </span>
            </small>
        </div>
    )
}
